import { format } from "date-fns";
import { FC, FormEvent } from "react";
import { ActionModal } from "shared/components/ActionModal";
import {
  Field,
  FieldErrorText,
  Textarea,
  tokens,
} from "shared/components/Spor";
import { useTypedForm } from "shared/hooks/useTypedForm";
import { z } from "zod";
import { useISirklogMessagePublish } from "features/CenterContent/RoleContent/ISirk/useISirkData";
import { css } from "styled-components";
import { useTranslation } from "react-i18next";

type ISirkLogMessageModalProps = {
  isirkId: string;
  open: boolean;
  setopen: (open: boolean) => void;
};

const _createIncidentLogMessageSchema = z.object({
  message: z.string().min(1),
  isirkId: z.string().nullable(),
  incidentId: z.string().nullable(),
  date: z.string().nullable(),
});

export type CreateIncidentLogMessageSchema = z.infer<
  typeof _createIncidentLogMessageSchema
>;

const NewLogMessageModal: FC<ISirkLogMessageModalProps> = ({
  isirkId,
  open,
  setopen,
}) => {
  const { t } = useTranslation();
  const { postIncidentLog, mutationStatus, resetMutation } =
    useISirklogMessagePublish();

  const {
    values,
    handleChange,
    reset: resetForm,
    startValidation,
    errors: formErrors,
  } = useTypedForm<{ message: string }>({ message: "" }, (formValues) => ({
    message:
      formValues.message.trim().length > 0
        ? undefined
        : t("Dette feltet må fylles ut"),
  }));

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!values.message.trim()) {
      startValidation();
      return;
    }

    const payload: CreateIncidentLogMessageSchema = {
      message: values.message,
      isirkId,
      incidentId: null,
      date: format(Date.now(), "yyyy-MM-dd"),
    };
    postIncidentLog(payload);
  };

  const onClose = () => {
    setopen(false);
    resetForm();
    resetMutation();
  };

  return open ? (
    <ActionModal
      title="Skriv ny loggmelding på I-Sirk"
      actionTitle="Send loggmelding"
      onClose={onClose}
      onSubmit={onSubmit}
      loading={mutationStatus === "pending"}
      isSuccess={mutationStatus === "success"}
      isError={mutationStatus === "error"}
      successMessage="Loggmelding sendt"
      failureMessage="Kunne ikke sende loggmelding. Prøv igjen, eller kontakt IT hvis feilen vedvarer."
      contentBodyStyle={css`
        padding-bottom: ${tokens.default.size.spacing[6]};
      `}
    >
      <Field invalid={!!formErrors.message} w="100%">
        <Textarea
          label="Skriv ny loggmelding"
          name="logmessage"
          value={values.message}
          onChange={(e) => handleChange({ message: e.target.value })}
          onBlur={() => startValidation()}
          aria-invalid={formErrors.message !== undefined}
          aria-errormessage="logmessage-error-message"
          minHeight="10rem"
        />
        <FieldErrorText>{formErrors.message}</FieldErrorText>
      </Field>
    </ActionModal>
  ) : null;
};

export default NewLogMessageModal;
